import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Strip empty strings from request data (converts "" to undefined so they're omitted)
function stripEmptyStrings(data: unknown): unknown {
  if (data === null || data === undefined) return data;
  // Pass FormData / Blob / File / ArrayBuffer through untouched — Object.entries
  // returns [] for these and would silently turn the body into "{}".
  if (typeof FormData !== 'undefined' && data instanceof FormData) return data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) return data;
  if (typeof ArrayBuffer !== 'undefined' && (data instanceof ArrayBuffer || ArrayBuffer.isView(data as ArrayBufferView))) return data;
  if (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams) return data;
  if (Array.isArray(data)) return data.map(stripEmptyStrings);
  if (typeof data === 'object') {
    const cleaned: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      if (value === '') continue;
      cleaned[key] = stripEmptyStrings(value);
    }
    return cleaned;
  }
  return data;
}

// Request interceptor: attach access token + clean payload
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data && typeof config.data === 'object' && ['post', 'put', 'patch'].includes(config.method ?? '')) {
      config.data = stripEmptyStrings(config.data);
    }
    if (config.params && typeof config.params === 'object') {
      config.params = stripEmptyStrings(config.params);
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: handle 401 → refresh token → retry
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Unauthenticated auth endpoints: a 401 here means "bad credentials", not an
// expired session — so don't run the refresh/redirect flow (which would clear
// auth and hard-redirect to /login, eating the caller's error). Let it reject
// so the caller (e.g. the login form) can show the error.
const AUTH_NO_REFRESH = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/forgot-password', '/auth/reset-password'];

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const isAuthEndpoint = AUTH_NO_REFRESH.some((p) => (originalRequest?.url || '').includes(p));

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('crm_refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(
          `${import.meta.env.VITE_API_URL || '/api/v1'}/auth/refresh`,
          { refreshToken },
        );

        const { accessToken, refreshToken: newRefreshToken } = response.data.data;

        useAuthStore.getState().setAccessToken(accessToken);
        localStorage.setItem('crm_refresh_token', newRefreshToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        processQueue(null, accessToken);

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError);
        useAuthStore.getState().clearAuth();
        localStorage.removeItem('crm_refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default api;
